import React, { useMemo, useRef, useState } from 'react';
import ReactMapGL, { Layer, Marker, Source } from 'react-map-gl';
import cn from 'classnames';
import Icon from 'components/shared/salon-marker-black';
import UserIcon from 'components/shared/user-marker';
import KeuneIconWhite from 'components/shared/keune-icon-white';
import useIsMobile from 'hooks/utils/useIsMobile';
import { handleGetDistance } from 'lib/utils/salonsHelpers';
import KeuneIconGold from 'components/shared/keune-icon-gold';
import { useTranslations } from 'contexts';
import styles from './map.module.scss';

const Map = ({
  hovered,
  viewport,
  initialViewport,
  allSalons,
  salonsInRadius,
  setHovered,
  setViewport,
  setInitialViewport,
  handleSalonFocus,
}) => {
  const translations = useTranslations();
  const isMobile = useIsMobile();
  const [hoverPin, setHoverPin] = useState(null);
  const mapRef = useRef();
  const renderClusterThreshold = 11.5;
  const renderMarkersThreshold = 11;

  const setActiveState = index => {
    setHovered(index);
    setHoverPin(index);
  };

  const setInactiveState = () => {
    setHovered(null);
    setHoverPin(null);
  };

  const selectCluster = event => {
    const feature = event.features[0];
    const map = mapRef.current?.getMap();
    if (!feature || !map) return;

    const cluster = feature.properties.cluster_id;
    map.getSource('salons').getClusterExpansionZoom(cluster, (err, zoom) => {
      if (err) return;

      const [longitude, latitude] = feature.geometry.coordinates;
      setInitialViewport({ ...initialViewport, latitude, longitude, zoom });
      map.easeTo({
        center: feature.geometry.coordinates,
        duration: 500,
        zoom,
      });
      setTimeout(() => {
        setViewport({ ...viewport, latitude, longitude, zoom });

      }, 750);
    });
  };

  const salonsByDistance = useMemo(() => {
    if (salonsInRadius.length > 0) {
      /** Map salons with distance and sort them from closest to furthest */
      const salonsWithDistanceAdded = salonsInRadius
        .map(salon => {
          const formattedDistance = (
            handleGetDistance(
              {
                latitude: salon?.position?.latitude,
                longitude: salon?.position?.longitude,
              },
              initialViewport
            ) / 1000
          ).toFixed(1);

          return { ...salon, formattedDistance };
        })
        .sort((salonA, salonB) => salonA.formattedDistance - salonB.formattedDistance);

      /** Return closest ambassador salon first, then spread the rest of the salons */
      const closestAmbassadorSalon = salonsWithDistanceAdded.find(salon => salon.salonLoyalty);
      const otherSalons = salonsWithDistanceAdded.filter(salon => salon !== closestAmbassadorSalon);

      if (closestAmbassadorSalon !== undefined) return [closestAmbassadorSalon, ...otherSalons];

      return otherSalons;
    }

    return [];
  }, [salonsInRadius, initialViewport]);

  /** Memoized variable to create layer with all salons clustered */
  const geojsonData = useMemo(() => {
    const salonGeojson = allSalons.map((salon, index) => ({
      type: 'Feature',
      properties: {
        salon,
        index,
      },
      geometry: {
        type: 'Point',
        coordinates: [salon.position.longitude, salon.position.latitude],
      },
    }));

    return {
      type: 'FeatureCollection',
      features: salonGeojson,
    };
  }, [allSalons]);

  return (
    <div className={styles.mapContainer}>
      <ReactMapGL
        zoom={viewport?.zoom}
        width={viewport?.width}
        height={viewport?.height}
        latitude={viewport?.latitude}
        longitude={viewport?.longitude}
        mapStyle="mapbox://styles/mapbox/light-v11"
        onViewportChange={newViewport => setViewport(newViewport)}
        mapboxApiAccessToken={process.env.NEXT_PUBLIC_MAPBOX_API_ACCESS_TOKEN}
        interactiveLayerIds={viewport.zoom < renderClusterThreshold ? ['salon-clusters'] : null}
        onClick={event => selectCluster(event)}
        onMouseEnter={event => setActiveState(event)}
        onMouseLeave={setInactiveState}
        ref={mapRef}
        maxZoom={16}
        minZoom={6.5}
        onLoad={() => {
          const attr = mapRef.current
            ?.getMap()
            ?.getContainer()
            ?.querySelector('.mapboxgl-ctrl-attrib-inner');
          if (attr) attr.removeAttribute('role');
        }}
      >
        <button className={styles.userContainer} type="button" aria-label={translations?.accessibility?.myLocation || 'My location'}>
          <Marker
            key="userLocation"
            longitude={initialViewport?.longitude}
            latitude={initialViewport?.latitude}
            className={cn(styles.userMarker)}
          >
            <UserIcon
              width="2.8rem"
              height={isMobile ? `3.6rem` : '2.8rem'}
              type="pin"
              className={cn(styles.icon)}
            />
          </Marker>
        </button>
        <Source id="salons" type="geojson" data={geojsonData} cluster clusterRadius={90}>
          {/** Layer responsible for rendering the clusters themselves */}
          {viewport.zoom < renderClusterThreshold && (
            <>
              <Layer
                id="salon-clusters"
                type="circle"
                source="salons"
                filter={['has', 'point_count']}
                paint={{
                  'circle-color': '#3c3c3b',
                  'circle-radius': ['step', ['get', 'point_count'], 15, 200, 20, 350, 25],
                }}
              />
              {/** Layer responsible for rendering the amount of points/salons within the cluster */}
              <Layer
                id="cluster-count"
                type="symbol"
                source="salons"
                filter={['has', 'point_count']}
                paint={{
                  'text-color': '#ffffff',
                }}
                layout={{
                  'text-field': `{point_count_abbreviated}`,
                  'text-size': 12,
                }}
              />
            </>
          )}
        </Source>
        {viewport.zoom >= renderMarkersThreshold &&
          salonsByDistance.map((salon, index) => {
            const idx = index;
            const active = hovered;
            const [lat, lon] = [
              Number(salon.position.latitude),
              Number(salon.position.longitude),
            ];

            return (
              <Marker
                key={salon.salonNo}
                longitude={lon}
                latitude={lat}
                className={cn(styles.marker, {
                  [styles.hovered]: active === idx || hoverPin === idx,
                })}
              >
                <div
                  onClick={() => handleSalonFocus(salon, idx)}
                  onKeyPress={() => handleSalonFocus(salon, idx)}
                  role="button"
                  tabIndex="0"
                  onMouseEnter={() => setActiveState(idx)}
                  onMouseLeave={() => setInactiveState()}
                >
                  <Icon
                    width="2.8rem"
                    height="3.6rem"
                    type="pin"
                    className={cn({
                      [styles.hovered]: active === idx || hoverPin === idx,
                      [styles.ambassador]: salon.salonLoyalty || salon.client100,
                    })}
                  />
                  {(salon.salonLoyalty === true || salon.client100 === true) && (
                    <span
                      className={cn(styles.span, {
                        [styles.ambassadorIcon]: salon.salonLoyalty || salon.client100,
                      })}
                    >
                      {active === idx || hoverPin === idx ? (
                        <KeuneIconGold />
                      ) : (
                        <KeuneIconWhite />
                      )}
                    </span>
                  )}
                  <span
                    className={cn(styles.span, {
                      [styles.ambassadorSpan]: salon.salonLoyalty || salon.client100,
                      [styles.hovered]: active === idx || hoverPin === idx,
                    })}
                  >
                    {idx + 1}
                  </span>
                </div>
              </Marker>
            );
          })}
      </ReactMapGL>
    </div>
  );
};

export default Map;
